import React, { useState, useRef, useEffect, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { AiFillEyeInvisible } from "react-icons/ai";
import { BsFillEyeFill } from "react-icons/bs";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { getMeTheChartType, minutesToHHMM, secondsToHHMM } from "../Utilitie";
import { IoBarChartSharp } from "react-icons/io5";
import { FaChartLine } from "react-icons/fa";

const GroupedBarChart = ({
  setSelectedHead,
  rotateText,
  trend,
  data,
  decimal,
  subFrom,
  from,
  chartType,
  height,
  selectedHead,
}) => {
  const [isHorizontal, setIsHorizontal] = useState(false);
  const [chartHeight, setChartHeight] = useState(200);
  const chartRef = useRef(null);
  const [selectedLegend, setSelectedLegend] = useState({});
  const [hideShowLegend, setHideShowLegend] = useState(true);
  const [chartTypeMaster, setChartTypeMaster] = useState(chartType);
  const [zoomState, setZoomState] = useState({ start: 0, end: null });
  const [colorCode, setColorCode] = useState({});
  const isMounted = useRef(true);
  let timeFormate = false;
  if (from === "Block_T" || from === "PDD_T") {
    timeFormate = true;
  }

  // Generate color map for time slots + FYs
  function generateColorMap(financialYears, timeSlots) {
    const baseColors = {
      "00_08": [
        "rgba(81, 161, 227, 0.5)",
        "rgba(81, 161, 227, 1)",
        "rgba(13, 125, 217, 1)",
      ],
      "08_16": [
        "rgba(48, 150, 56, 0.5)",
        "rgba(48, 150, 60, 1)",
        "rgba(5, 131, 19, 1)",
      ],
      "16_24": [
        "rgba(225, 61, 61, 0.5)",
        "rgba(225, 61, 61, 1)",
        "rgba(182, 8, 8, 1)",
      ],
    };

    const result = {};
    timeSlots.forEach((slot) => {
      financialYears.forEach((fy, index) => {
        const colorArray = baseColors[slot] || ["#9e9e9e"];
        const color = colorArray[index % colorArray.length];
        result[`${slot} in ${fy}`] = color;
      });
    });
    return result;
  }

  useEffect(() => {
    if (data) {
      let a = generateColorMap(
        [...new Set(data.dimensions.map((a) => a.slice(9, a.length)))].filter(
          (a) => a !== ""
        ),
        [...new Set(data.dimensions.map((a) => a.slice(0, 5)))].filter(
          (a) => a !== "produ"
        )
      );
      setColorCode(a);
    }
  }, [data]);

  useEffect(() => {
    setIsHorizontal(
      from === "AchievedChartData" || from === "AchievedChartDataCHW"
    );
  }, [from]);

  useEffect(() => {
    if (from === "AssetFailure" || from === "IRE") {
      setChartHeight(height || 410);
    } else if (from === "Block_T" || from === "Block_C") {
      setChartHeight(height || 200);
    } else {
      setChartHeight(isHorizontal ? 70 : 410);
    }
  }, [from, isHorizontal, height]);

  const calculateEnd = (seriesLength) => {
    if (seriesLength >= 100) return 10;
    if (seriesLength > 60) return 20;
    if (seriesLength > 30) return 30;
    return 100;
  };

  // Main Bar Series (one per legend/column)
  const dataSeries = useMemo(() => {
    return (data.dimensions || []).slice(1).map((field, index) => ({
      type: chartTypeMaster,
      encode: isHorizontal
        ? { y: data.dimensions[0], x: field }
        : { x: data.dimensions[0], y: field },
      name: field,
      animationDuration: 1500,
      animationEasing: "cubicOut",
      animationDelay: index * 300,
      itemStyle: {
        color: colorCode[field] || null,
        shadowColor: "rgba(0, 0, 0, 0.4)",
        shadowBlur: 5,
        shadowOffsetX: 4,
        shadowOffsetY: 0,
      },
      label: {
        show: true,
        position: isHorizontal ? "right" : "top",
        formatter: (params) => {
          const value = params.value[field];
          if (
            [
              "commodity",
              "7ACHWeight",
              "AchievedChartDataCHW",
              "7ACHWEIGHTSiding",
            ].includes(from)
          ) {
            return `${(value / 1000000).toFixed(2)}M`;
          }
          if (
            ["7ALoading", "AchievedChartData", "7AWagonSiding"].includes(from)
          ) {
            return `${(value / 1000).toFixed(2)}K`;
          }
          if (from === "PDD_T") return minutesToHHMM(value, false);
          if (from === "Block_T") return secondsToHHMM(value);
          if (from === "Block_C" || from === "PDD_C")
            return Number(value).toFixed(decimal ? decimal : 0);
          if (from === "IRE") return `${Number(value).toFixed(1)}%`;
          return from === "AssetFailure"
            ? Number(value)
            : Number(value).toFixed(2);
        },
        fontSize: 10,
        color: "black",
        offset: ["AchievedChartData", "AchievedChartDataCHW"].includes(from)
          ? [10, 0]
          : [0, 0],
      },
    }));
  }, [data, chartTypeMaster, isHorizontal, from, colorCode]);

  // CORRECTED: One trend line per CATEGORY (row), connecting across legends (columns)
  const trendSeries = useMemo(() => {
    if (!trend || !data?.series || data.series.length === 0) return [];

    const legendColumns = data.dimensions.slice(1);

    // Choose which column contains the values you want to trend on
    // Most common cases:
    // 1. Last column → latest FY / Target
    // 2. A column that contains "target" in name
    let trendColumnKey = legendColumns[legendColumns.length - 1]; // default: last column

    // Optional: auto-detect column with "target" (uncomment if needed)
    // const targetCol = legendColumns.find(c => c.toLowerCase().includes('target'));
    // if (targetCol) trendColumnKey = targetCol;

    // Extract the values from that column for every category (row)
    const trendValues = data.series.map((row) => row[trendColumnKey]);

    return [
      {
        name: "Trend", // Only ONE legend entry
        type: "line",
        data: trendValues,
        connectNulls: false,
        symbol: "circle",
        symbolSize: 12,
        lineStyle: {
          color: "#ff4500", // Bright red-orange
          width: 5,
          type: "solid",
          shadowBlur: 12,
          shadowColor: "rgba(255, 69, 0, 0.5)",
        },
        itemStyle: {
          color: "#ff4500",
          borderColor: "#ffffff",
          borderWidth: 3,
        },
        emphasis: {
          lineStyle: { width: 7 },
        },
        zlevel: 10, // Render on top of everything
        z: 1000,

        // This is the magic that lifts the line just above the bars
        // We add a tiny visual offset (5% higher) – only for display
        data: trendValues.map((val) => ({
          value: val === null || val === undefined ? null : val * 1.05, // 5% higher
          // Store original value for tooltip & label
          originalValue: val,
        })),

        // Show the REAL value on top of each point
        label: {
          show: true,
          position: "top",
          fontWeight: "bold",
          color: "#d4380d",
          fontSize: 11,
          formatter: (params) => {
            const orig = params.data?.originalValue;
            if (orig === null || orig === undefined) return "";
            // Reuse your existing formatting logic
            if (
              ["7ALoading", "AchievedChartData", "7AWagonSiding"].includes(from)
            ) {
              return `${(orig / 1000).toFixed(1)}K`;
            }
            return orig;
          },
        },
      },
    ];
  }, [data, trend, from]);

  const getOption = useMemo(() => {
    const categoryAxis = isHorizontal ? "yAxis" : "xAxis";
    const valueAxis = isHorizontal ? "xAxis" : "yAxis";
    const seriesLength = data.series?.length || 0;
    const initialEnd = zoomState.end ?? calculateEnd(seriesLength);

    return {
      animation: true,
      animationDuration: 1000,
      animationEasing: "cubicOut",
      dataset: {
        source: data.series || [],
        dimensions: data.dimensions || [],
      },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: function (params) {
          let str = `<strong>${params[0].axisValueLabel}</strong><br/>`;
          params.forEach((p) => {
            if (p.seriesName === "Trend") {
              const realVal = p.data?.originalValue ?? p.value;
              const formatted = [
                "7ALoading",
                "AchievedChartData",
                "7AWagonSiding",
              ].includes(from)
                ? `${(realVal / 1000).toFixed(1)}K`
                : realVal;
              str += `${p.marker} <strong>Trend</strong>: ${formatted}<br/>`;
            } else if (p.seriesType === "bar") {
              str += `${p.marker} ${p.seriesName}: ${
                p.value[p.seriesName] || p.value
              }<br/>`;
            }
          });
          return str;
        },
      },
      legend: {
        show: from !== "AchievedChartData" && from !== "AchievedChartDataCHW",
        selected: selectedLegend,
        type: "scroll",
        top: "1%",
      },
      [categoryAxis]: {
        type: "category",
        show: from !== "AchievedChartData" && from !== "AchievedChartDataCHW",
        axisLabel: {
          interval: 0,
          color: "black",
          rotate: rotateText ? rotateText : from === "AssetFailure" ? 20 : 0,
        },
      },
      [valueAxis]: {
        show: from !== "AchievedChartData" && from !== "AchievedChartDataCHW",
        type: "value",
        axisLabel: {
          color: "black",
          ...(timeFormate && {
            formatter: (value) =>
              from === "Block_T" ? secondsToHHMM(value) : minutesToHHMM(value),
          }),
        },
      },
      grid: {
        top: ["AchievedChartData", "AchievedChartDataCHW"].includes(from)
          ? "-5%"
          : ["Block_T", "Block_C"].includes(from)
          ? "25%"
          : "15%",
        left: ["AchievedChartData", "AchievedChartDataCHW"].includes(from)
          ? "-15%"
          : ["PDD_C", "IRE", "PDD_T", "Block_T"].includes(from)
          ? "1%"
          : "5%",
        right: ["AchievedChartData", "AchievedChartDataCHW"].includes(from)
          ? "20%"
          : "5%",
        bottom: isHorizontal
          ? "5%"
          : ["PDD_C", "IRE", "PDD_T", "Block_T"].includes(from)
          ? "1%"
          : "15%",
        containLabel: true,
      },
      dataZoom: [
        {
          show:
            [
              "AssetFailure",
              "7ALoading",
              "7ACHWeight",
              "7AWagonSiding",
              "7ACHWEIGHTSiding",
            ].includes(from) && seriesLength >= 5,
          type: "slider",
          [`${categoryAxis}Index`]: 0,
          start: zoomState.start,
          end: initialEnd,
          height: 30,
          bottom: 10,
          handleSize: 20,
          showDetail: true,
        },
        {
          type: "inside",
          [`${categoryAxis}Index`]: 0,
          start: zoomState.start,
          end: initialEnd,
          zoomOnMouseWheel: true,
          moveOnMouseMove: true,
        },
      ],
      series: trend ? [...dataSeries, ...trendSeries] : dataSeries,
      animationDuration: 2000,
      animationEasing: "cubicOut",
      animationDurationUpdate: 1500,
      animationEasingUpdate: "cubicOut",
    };
  }, [
    data,
    from,
    chartTypeMaster,
    isHorizontal,
    selectedLegend,
    zoomState,
    dataSeries,
    trendSeries,
    trend,
  ]);

  // Legend sync
  useEffect(() => {
    if (!data?.dimensions || !chartRef.current) return;
    const chartInstance = chartRef.current.getEchartsInstance();
    if (!chartInstance) return;

    const legends = data.dimensions.slice(1);
    const newSelectedLegend = {};
    legends.forEach((legend) => {
      newSelectedLegend[legend] = selectedLegend[legend] ?? hideShowLegend;
    });

    setSelectedLegend(newSelectedLegend);

    legends.forEach((legend) => {
      chartInstance.dispatchAction({
        type: newSelectedLegend[legend] ? "legendSelect" : "legendUnSelect",
        name: legend,
      });
    });
  }, [data, hideShowLegend]);

  const toggleLegends = () => {
    const chartInstance = chartRef.current?.getEchartsInstance();
    if (!chartInstance) return;

    const newHideShowLegend = !hideShowLegend;
    setHideShowLegend(newHideShowLegend);

    const newSelected = {};
    (data.dimensions || []).slice(1).forEach((legend) => {
      newSelected[legend] = newHideShowLegend;
      chartInstance.dispatchAction({
        type: newHideShowLegend ? "legendSelect" : "legendUnSelect",
        name: legend,
      });
    });
    setSelectedLegend(newSelected);
  };

  useEffect(() => {
    const chartInstance = chartRef.current?.getEchartsInstance();
    if (!chartInstance) return;

    const handler = (params) => {
      if (!isMounted.current) return;
      setSelectedLegend(params.selected);
      setHideShowLegend(Object.values(params.selected).every((v) => v));
    };

    chartInstance.on("legendselectchanged", handler);
    return () => chartInstance.off("legendselectchanged", handler);
  }, []);

  useEffect(() => {
    if (!data) return;
    const chartInstance = chartRef.current?.getEchartsInstance();
    if (!chartInstance) return;

    const handler = (params) => {
      const newStart =
        params.start ?? params.batch?.[0]?.start ?? zoomState.start;
      const newEnd = params.end ?? params.batch?.[0]?.end ?? zoomState.end;
      setZoomState({ start: newStart, end: newEnd });
    };

    chartInstance.on("datazoom", handler);
    return () => chartInstance.off("datazoom", handler);
  }, [data, zoomState]);

  const onChartClick = (params) => {
    if (from === "AssetFailure" && params?.name) {
      setSelectedHead({
        ASSET_GROUP:
          subFrom === "asset" ? params.name : selectedHead?.ASSET_GROUP || "",
        F_YEAR: params.seriesName,
        MAIN_CAUSE_DESC: "",
        Month: "",
        SUB_CAUSE_DESC: "",
        SECTION_NAME:
          subFrom === "Section"
            ? params.name
            : selectedHead?.SECTION_NAME || "",
      });
    }
  };

  const onEvents = { click: onChartClick };

  return (
    <div className="position-relative">
      {from !== "AchievedChartData" && from !== "AchievedChartDataCHW" && (
        <div className="position-absolute top-0 end-0" style={{ zIndex: 2 }}>
          <OverlayTrigger
            delay={{ show: 100, hide: 200 }}
            overlay={
              <Tooltip>{hideShowLegend ? "Hide All" : "Show All"}</Tooltip>
            }
          >
            <button
              className="btn btn-outline-dark btn-sm me-1"
              onClick={toggleLegends}
            >
              {hideShowLegend ? <AiFillEyeInvisible /> : <BsFillEyeFill />}
            </button>
          </OverlayTrigger>

          <OverlayTrigger
            delay={{ show: 100, hide: 200 }}
            overlay={
              <Tooltip>
                {chartTypeMaster === "bar" ? "Switch to line" : "Switch to bar"}
              </Tooltip>
            }
          >
            <button
              className="btn btn-outline-dark btn-sm"
              onClick={() =>
                setChartTypeMaster(chartTypeMaster === "bar" ? "line" : "bar")
              }
            >
              {chartTypeMaster === "line" ? (
                <IoBarChartSharp />
              ) : (
                <FaChartLine />
              )}
            </button>
          </OverlayTrigger>
        </div>
      )}

      <ReactECharts
        ref={chartRef}
        option={getOption}
        style={{ height: chartHeight }}
        onEvents={onEvents}
        notMerge={true}
      />
    </div>
  );
};

export default GroupedBarChart;
